import { useContext, type FC } from 'react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, str } from '@/app/lib/a2ui/props.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { MarkdownText } from '@/app/lib/a2ui/blocks/MarkdownText.tsx';
import { SuggestedReply } from '@/app/lib/a2ui/blocks/SuggestedReply.tsx';
import { ExploreContent } from '@/app/lib/a2ui/ExploreContent.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { SurfaceHeadingLevel } from '@/app/lib/a2ui/blocks/heading-context.ts';
import './care.css';

export const CareResponse: FC<A2uiNodeViewProps> = ({ renderChildren }) => <>{renderChildren()}</>;

function usableImage(value: unknown) {
  const src = str(value);
  try {
    const url = new URL(src);
    return url.protocol === 'https:' && url.pathname.length > 1 ? src : '';
  } catch { return ''; }
}

export const CareContent: FC<A2uiNodeViewProps> = ({ node }) => {
  const props = node.props;
  const dispatch = useSurfaceAction();
  const level = useContext(SurfaceHeadingLevel);
  const Heading = level === 1 ? 'h1' : 'h2';
  const Subheading = level === 1 ? 'h2' : 'h3';
  const image = usableImage(props.imageSrc);
  const blocks = arr(props.blocks).filter(isRecord);
  const replies = arr(props.replies).filter(isRecord);
  return (
    <article className="aster-care w-full min-w-0 text-foreground">
      <div className="aster-care-intro" data-image={!!image}>
        <div className="aster-care-copy">
          {str(props.highlight) && <Heading className="aster-care-heading font-display">{str(props.highlight)}</Heading>}
          {str(props.message) && <MarkdownText text={str(props.message)} size="base" className="aster-care-prose" />}
          {replies.length > 0 && <div className="aster-care-replies">
            {replies.map((reply, index) => <SuggestedReply key={str(reply.id) || index} disabled={!dispatch || !str(reply.id) || !str(reply.label)} onClick={() => dispatch?.('discussCare', { subjectId: str(props.subjectId), replyId: str(reply.id) }, str(reply.label))}>{str(reply.label)}</SuggestedReply>)}
          </div>}
        </div>
        {image && <figure className="aster-care-photo"><img src={image} alt={str(props.imageAlt)} className="h-full w-full object-cover" /></figure>}
      </div>
      {blocks.length > 0 && <div className="aster-care-blocks">
        {blocks.map((block, index) => {
          const src = usableImage(block.imageSrc);
          const content = <>
            {src && <img className="aster-care-treatment-image" src={src} alt={str(block.imageAlt)} loading="lazy" />}
            <div className="aster-care-details">
              {(str(block.duration) || str(block.price)) && <div className="flex flex-wrap justify-between gap-3 text-base text-muted-foreground tabular-nums"><span>{str(block.duration)}</span><span>{str(block.price)}</span></div>}
              {str(block.highlight) && <Subheading className="font-display text-3xl leading-tight">{str(block.highlight)}</Subheading>}
              {str(block.message) && <MarkdownText text={str(block.message)} size="base" className="aster-care-prose" />}
            </div>
          </>;
          return block.kind === 'treatment' ? <ExploreContent key={str(block.id) || index} node={node} subject={str(block.highlight)} selection={{ id: block.id, name: block.highlight, message: block.message, duration: block.duration, price: block.price }} surrounding={{ subjectId: props.subjectId }} className="aster-care-treatment overflow-hidden border border-border bg-card text-card-foreground">{content}</ExploreContent> : <section key={str(block.id) || index} className="aster-care-note">{content}</section>;
        })}
      </div>}
    </article>
  );
};
